import { ApiError } from "@repo/api-client";

/**
 * Client half of session revocation (features/roles-and-permissions.md R7). The server ends sessions
 * on removal / suspension / force-reset; the SPA reacts on the NEXT request: any query or mutation
 * that comes back 401 fires [notifyUnauthorized], and [SessionExpiryHandler] clears the cache and
 * routes to /login. Kept as a tiny pub-sub so the QueryClient (created outside the router) can signal
 * a component that owns `useNavigate` — no `window.location` hard-reload, no router coupling here.
 */
type Listener = () => void;

const listeners = new Set<Listener>();

export function onUnauthorized(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function notifyUnauthorized(): void {
  for (const listener of listeners) listener();
}

/** True when a rejected query/mutation is a 401 (a revoked or expired session). */
export function isUnauthorized(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

/**
 * Navigation state [SessionExpiryHandler] attaches to its /login redirect. The server has just
 * rejected the session, so Login must re-authenticate even though the client may still hold a user:
 * react-oidc-context keeps `isAuthenticated` true once the access token expires, and trusting it
 * bounced /login -> / -> 401 -> /login until the user reloaded.
 */
export const SESSION_ENDED_STATE = { sessionEnded: true } as const;

/** True when a location's state came from a 401 sign-out ([SESSION_ENDED_STATE]). */
export function isSessionEnded(state: unknown): boolean {
  return (
    typeof state === "object" &&
    state !== null &&
    (state as { sessionEnded?: unknown }).sessionEnded === true
  );
}
